// SignupForm.js
import React from 'react';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import { Link } from 'react-router-dom';
import SectionHeader from './partials/SectionHeader';
import Input from '../elements/Input';
import Button from '../elements/Button';
import api from '../../api/http';
import { API_ENDPOINTS } from '../../api/endpoints';
import { ERROR_MESSAGES } from '../../api/errorMsg';
import { withLanguage } from '../../context/LanguageContext';

const propTypes = {
  ...SectionProps.types,
};

const defaultProps = {
  ...SectionProps.defaults,
};

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,}$/;

class SignupForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      loading: false,
      error: '',
      success: '',
      showPasswordHint: false,
    };
    this.controller = null;
  }

  componentDidMount() {
    // Check if planId is passed via router state
    if (this.props.location && this.props.location.state && this.props.location.state.planId) {
      this.setState({ planId: this.props.location.state.planId });
    }
  }

  handleChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value });

    // ✅ Live password validation
    if (name === "password") {
      if (value && !passwordRegex.test(value)) {
        this.setState({
          error: this.props.text("パスワード形式が正しくありません", "The password format is invalid.")
        });
      } else {
        this.setState({ error: "" });
      }
    }
  };



  // --- Step 3 Logic (Final Submit) ---
  handleSignup = async (e) => {
    e.preventDefault();
    this.setState({ error: '', success: '', loading: true });

    const { email, password } = this.state;

    // ✅ Password validation
    if (!passwordRegex.test(password)) {
      this.setState({
        error: this.props.text("パスワードは8文字以上で、大文字・小文字・数字をそれぞれ1つ以上含める必要があります。", "Use at least 8 characters, including uppercase, lowercase, and a number."),
        success: '',
        loading: false
      });
      return;
    }
    // Validation for Full Trial

    this.controller = new AbortController();
    const signal = this.controller.signal;

    try {
      // NOTE: In production, pass trialType and planId to backend
      const payload = {
        email,
        password,
      };

      const res = await api.post(
        API_ENDPOINTS.REGISTER,
        payload,
        { signal }
      );

      const data = res.data;
      console.log('Signup success', data);

      this.setState(
        {
          success: data?.message || this.props.text('アカウントが作成されました。メールを確認してください。', 'Your account was created. Please check your email.'),
          error: '',
        },
        () => {
          if (this.props.history && this.props.history.push) {
            this.props.history.push('/otp-verify', { email });
          } else {
            window.location.href = '/otp-verify';
          }
        }
      );
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        console.log('Signup request aborted');
      } else {
        let msg = ERROR_MESSAGES.SIGNUP.UNKNOWN;
        if (err.response) {
          const { status, data } = err.response;
          const backendMsg = data?.message || data?.error;
          if (backendMsg) msg = backendMsg;
          else if (status === 409) msg = ERROR_MESSAGES.SIGNUP.EMAIL_ALREADY_EXISTS;
          else if (status >= 500) msg = ERROR_MESSAGES.SIGNUP.NETWORK_ERROR;
        } else if (err.message === 'Network Error') {
          msg = ERROR_MESSAGES.SIGNUP.NETWORK_ERROR;
        }
        this.setState({ error: msg, success: '' });
      }
    } finally {
      this.setState({ loading: false });
      this.controller = null;
    }
  };

  componentWillUnmount() {
    if (this.controller) this.controller.abort();
  }

  // --- Render Helpers ---

  renderProgressBar() {
    const { step } = this.state;
    return (
      <div className="signup-progress mb-24" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: step >= 1 ? '#1f63d6' : '#ddd' }} />
        <div style={{ width: '30px', height: '2px', background: '#ddd', alignSelf: 'center' }} />
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: step >= 2 ? '#1f63d6' : '#ddd' }} />
        <div style={{ width: '30px', height: '2px', background: '#ddd', alignSelf: 'center' }} />
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: step >= 3 ? '#1f63d6' : '#ddd' }} />
      </div>
    );
  }

  renderStep1() {
    const { text } = this.props;
    const { email, password, showPasswordHint, loading } = this.state;
    return (
      <form onSubmit={this.handleSignup}>
        <fieldset>
          <div className="mb-12">
            <Input
              name="email"
              type="email"
              placeholder={text("メールアドレス *", "Email address *")}
              value={email}
              onChange={this.handleChange}
              required
            />
          </div>
          <div className="mb-12">
            <Input
              name="password"
              type="password"
              placeholder={text("パスワード *", "Password *")}
              value={password}
              onChange={this.handleChange}
              onFocus={() => this.setState({ showPasswordHint: true })}
              onBlur={() => this.setState({ showPasswordHint: false })}
              required
            />
            {showPasswordHint && (
              <div className="hint-text text-xs mt-6">
                {text("パスワードは8文字以上で、大文字・小文字・数字をそれぞれ1つ以上含める必要があります。", "Use at least 8 characters, including uppercase, lowercase, and a number.")}
              </div>
            )}
          </div>
          <div className="mt-24 mb-32">
            <Button color="primary" wide type="submit" disabled={loading}>
              {loading ? text('処理中...', 'Processing...') : text('トライアルを開始', 'Start free trial')}
            </Button>
          </div>
        </fieldset>
      </form>
    );
  }


  render() {
    const { text } = this.props;
    const {
      className,
      topOuterDivider,
      bottomOuterDivider,
      topDivider,
      bottomDivider,
      hasBgColor,
      invertColor,
      ...props
    } = this.props;

    const { step, error, success } = this.state;

    const outerClasses = classNames(
      'signin section',
      topOuterDivider && 'has-top-divider',
      bottomOuterDivider && 'has-bottom-divider',
      hasBgColor && 'has-bg-color',
      invertColor && 'invert-color',
      className
    );

    const innerClasses = classNames(
      'signin-inner section-inner',
      topDivider && 'has-top-divider',
      bottomDivider && 'has-bottom-divider'
    );

    const sectionHeader = {
      title: text('無料トライアル', 'Free trial'),
    };

    return (
      <section {...props} className={outerClasses}>
        <div className="container">
          <div className={innerClasses}>
            <SectionHeader
              tag="h1"
              data={sectionHeader}
              className="center-content"
            />

            <div className="tiles-wrap">
              <div className="tiles-item">
                <div className="tiles-item-inner">

                  {/* Errors & Success */}
                  {error && (
                    <div className="error-text mb-12 text-xs text-center" style={{ color: '#ff5b5b' }}>
                      {error}
                    </div>
                  )}
                  {success && (
                    <div className="success-text mb-12 text-xs text-center">
                      {success}
                    </div>
                  )}

                  {/* Workflow Steps */}
                  {!success && (
                    <>
                      {!success && this.renderStep1()}
                    </>
                  )}

                  <div className="signin-bottom has-top-divider">
                    <div className="pt-32 text-xs center-content text-color-low">
                      {text('すでにアカウントをお持ちですか？', 'Already have an account?')}{' '}
                      <Link to="/login/" className="func-link">
                        {text('ログイン', 'Sign in')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    );
  }
}

SignupForm.propTypes = propTypes;
SignupForm.defaultProps = defaultProps;

export default withLanguage(SignupForm);
